import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, AlertTriangle, Loader2, ArrowLeft, Hexagon } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null, error: null });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null, error: "Missing session id." });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid" && data.status === "complete") {
          setState({ phase: "paid", data, error: null });
          // clear local cart id so cart resets
          try { localStorage.removeItem("gfx_cart_id"); } catch {}
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data, error: null });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", data, error: null });
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ phase: "error", data: null, error: e?.response?.data?.detail || e.message });
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-24 bg-[#0a0a0a] text-[#F5F5F7]" data-testid="checkout-success-page">
      <div className="absolute top-6 left-6 z-10">
        <Link to="/" className="flex items-center gap-2 group" data-testid="success-home-link">
          <Hexagon className="w-5 h-5 text-[#007AFF] group-hover:rotate-90 transition-transform duration-500" strokeWidth={1.6} />
          <span className="font-heading text-2xl tracking-wider">GRIPFORCE<span className="text-[#007AFF]">X</span></span>
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-xl border border-white/10 bg-[#0c0c0c] p-10 md:p-14 relative"
      >
        <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#007AFF]" />
        <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#007AFF]" />
        <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#007AFF]" />
        <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#007AFF]" />

        {state.phase === "checking" && (
          <Body
            icon={<Loader2 className="w-6 h-6 animate-spin" />}
            label="// Processing"
            title="VERIFYING PAYMENT"
            desc="Hang tight — confirming your order with Stripe. This usually takes a few seconds."
            testid="status-checking"
          />
        )}

        {state.phase === "paid" && (
          <Body
            icon={<Check className="w-6 h-6" strokeWidth={2.4} />}
            label="// Order Confirmed"
            title="DOMINANCE INCOMING."
            desc={`We've received your $${((state.data?.amount_total || 0) / 100).toFixed(2)} payment. Your GripForce X is being prepped for shipment.`}
            testid="status-paid"
            accent
            footer={
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 font-body" data-testid="success-session-id">
                Ref · {sessionId?.slice(-12)}
              </div>
            }
          />
        )}

        {state.phase === "expired" && (
          <Body
            icon={<AlertTriangle className="w-6 h-6" />}
            label="// Session Expired"
            title="THE TIMER RAN OUT."
            desc="Your Stripe checkout session has expired. Head back to the cart to try again."
            testid="status-expired"
          />
        )}

        {state.phase === "timeout" && (
          <Body
            icon={<AlertTriangle className="w-6 h-6" />}
            label="// Still Processing"
            title="HANG TIGHT."
            desc="Payment is taking longer than usual. Check your email for confirmation, or refresh this page."
            testid="status-timeout"
          />
        )}

        {state.phase === "error" && (
          <Body
            icon={<AlertTriangle className="w-6 h-6 text-red-400" />}
            label="// Error"
            title="SOMETHING WENT WRONG."
            desc={state.error || "We couldn't verify payment status."}
            testid="status-error"
          />
        )}

        <Link
          to="/"
          className="mt-10 inline-flex items-center gap-2 px-6 py-3 border border-white/15 hover:border-[#007AFF] hover:text-[#007AFF] text-xs uppercase tracking-[0.25em] font-body transition"
          data-testid="success-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to GripForce X
        </Link>
      </motion.div>
    </main>
  );
};

const Body = ({ icon, label, title, desc, accent, footer, testid }) => (
  <div data-testid={testid}>
    <div className={`w-14 h-14 flex items-center justify-center border ${accent ? "border-[#007AFF] text-[#007AFF] bg-[#007AFF]/10" : "border-white/15 text-white/80"} mb-8`}>
      {icon}
    </div>
    <div className="text-xs uppercase tracking-[0.3em] text-[#007AFF] mb-3 font-body">{label}</div>
    <h1 className="font-heading text-4xl sm:text-5xl leading-[0.95] mb-5">{title}</h1>
    <p className="text-white/55 leading-relaxed text-sm max-w-md">{desc}</p>
    {footer && <div className="mt-6">{footer}</div>}
  </div>
);

export default CheckoutSuccess;
